/*
 * Purpose: AutoBlocks composer — upcoming / NOW / history timeline layout
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAutoBlocksStore } from '@/stores/autoBlocksStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  Blocks,
  Plug,
  Globe,
  FlaskConical,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowRight,
  RotateCcw,
  ChevronDown,
  Map,
} from 'lucide-react';
import { GmailConnectorPanel } from '@/components/GmailConnectorPanel';
import { LinkedInConnectorPanel } from '@/components/LinkedInConnectorPanel';
import { McpConnectorPanel } from '@/components/McpConnectorPanel';
import { ConnectorLibraryPanel } from '@/components/ConnectorLibraryPanel';
import { PlanReadinessPanel } from '@/components/PlanReadinessPanel';
import { MissionRiskReviewPanel } from '@/components/mission/MissionRiskReviewPanel';
import { DirectorTrail } from '@/components/intent/DirectorTrail';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { SuperIntentInterpreter } from '@/components/intent/SuperIntentInterpreter';
import { IntentConfirmPanel } from '@/components/intent/IntentConfirmPanel';
import { ComposeTimelineRail } from '@/components/intent/ComposeTimelineRail';
import { ComposeStoryAnchor } from '@/components/intent/ComposeStoryAnchor';
import { ComposeFlowMapPreview } from '@/components/intent/ComposeFlowMapPreview';
import { ComposePlanRowCard } from '@/components/intent/ComposePlanRowCard';
import { ComposeFinalPlanPanel } from '@/components/intent/ComposeFinalPlanPanel';
import { AutopilotProgressCard } from '@/components/intent/AutopilotProgressCard';
import { ArtifactStateBadge } from '@/components/intent/ArtifactStateBadge';
import { attachWorkflowPreview } from '@/services/autoblocks/workflowAssembler';
import { rehydratePlanConnectors } from '@/services/autoblocks/planReadiness';
import { notifyMissionPlanUpdated } from '@/lib/missionNotifications';
import { buildComposeTimelineView } from '@/lib/composeTimeline';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { ContextualError } from '@/components/errors/ContextualError';

function StatusIcon({ status }: { status: string }) {
  if (status === 'pass') return <CheckCircle2 className="h-4 w-4 text-ab-green-sea shrink-0" />;
  if (status === 'simulated') return <FlaskConical className="h-4 w-4 text-blue-500 shrink-0" />;
  if (status === 'warn') return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
  return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
}

export function AutoBlocksComposer() {
  const navigate = useNavigate();
  const location = useLocation();
  const autoInterpretRan = useRef(false);
  const {
    phase,
    artifactState,
    composeMode,
    missionTrail,
    outcomeText,
    interpretation,
    plan,
    director,
    dryRunReport,
    isInterpreting,
    isDirectorWorking,
    isPlanning,
    isDryRunning,
    error,
    autopilot,
    isAutopilotRunning,
    isRunningLive,
    setOutcomeText,
    interpretOutcome,
    runAutopilotPipeline,
    confirmAndPlan,
    expressLaneProceed,
    runDryRunTest,
    approveAndOpenInBuilder,
    approveAndRunLive,
    openPlanInMissionMap,
    reset,
  } = useAutoBlocksStore();

  const jumpToNow = () => {
    document.getElementById('compose-now-zone')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openFlowMapInBuilder = () => {
    if (!plan) return;
    openPlanInMissionMap();
    navigate(REMEDIATION.mission);
  };
  const [describeOpen, setDescribeOpen] = useState(true);
  const [showOutcomeEditor, setShowOutcomeEditor] = useState(false);

  const confidencePct = plan ? Math.round(plan.confidence * 100) : 0;
  const isMission = artifactState === 'mission';
  const isWorking = isInterpreting || isPlanning || isDirectorWorking || phase === 'express_running';
  const autopilotBusy = autopilot && (isAutopilotRunning || isWorking || isDryRunning);

  const timeline = buildComposeTimelineView({
    phase,
    artifactState,
    hasPlan: Boolean(plan),
    hasDryRunReport: Boolean(dryRunReport),
    isDryRunning,
  });

  const workflowPreview = useMemo(
    () => (plan ? plan.workflowPreview ?? attachWorkflowPreview(plan).workflowPreview : null),
    [plan]
  );

  const blockCount = plan?.steps.length || workflowPreview?.nodes.length || 0;

  const showLinkedInPanel =
    plan &&
    (plan.steps.some((s) => s.subtype === 'mcp_tool' && /\blinkedin/i.test(String(s.config.mcpGoal ?? s.blockIntent ?? ''))) ||
      plan.connectors.some((c) => c.id.includes('linkedin')) ||
      /\blinkedin\b/i.test(plan.rawOutcome));

  const showMcpPanel =
    plan &&
    (plan.resolvedConnectors?.some((c) => c.connectorId === 'ab.mcp') ||
      plan.steps.some((s) => s.subtype === 'mcp_tool') ||
      plan.connectors.some((c) => c.id.includes('mcp')) ||
      /\bmcp\b|model context protocol/i.test(plan.rawOutcome));

  useEffect(() => {
    const state = location.state as {
      autoInterpret?: boolean;
      autopilot?: boolean;
      fromHome?: boolean;
    } | null;
    if (!state?.autoInterpret || autoInterpretRan.current || !outcomeText.trim()) return;
    autoInterpretRan.current = true;
    if (state.autopilot ?? autopilot) {
      void runAutopilotPipeline();
    } else {
      void interpretOutcome();
    }
  }, [location.state, outcomeText, interpretOutcome, runAutopilotPipeline, autopilot]);

  useEffect(() => {
    if (timeline.current.id !== 'describe') {
      setDescribeOpen(false);
    }
  }, [timeline.current.id]);

  const handleReset = () => {
    reset();
    setDescribeOpen(true);
    setShowOutcomeEditor(false);
  };

  const refreshPlanReadiness = (updated: NonNullable<typeof plan>) => {
    useAutoBlocksStore.setState({ plan: rehydratePlanConnectors(updated) });
    notifyMissionPlanUpdated('Plan updated', updated.interpretedGoal);
  };

  const finalPlanPanel =
    autopilot && plan && !autopilotBusy ? (
      <ComposeFinalPlanPanel
        outcomeText={outcomeText}
        plan={plan}
        dryRunReport={dryRunReport}
        isRunningLive={isRunningLive}
        error={error}
        onOutcomeChange={setOutcomeText}
        onPlanUpdate={refreshPlanReadiness}
        onRunLive={() =>
          void approveAndRunLive().then(() => {
            if (useAutoBlocksStore.getState().phase === 'approved') {
              navigate(REMEDIATION.dashboard);
            }
          })
        }
        onSaveOnly={() => {
          approveAndOpenInBuilder();
          navigate(REMEDIATION.flows);
        }}
        onRetryDryRun={() => void runDryRunTest()}
        onStartOver={handleReset}
      />
    ) : null;

  const describeEditor = (
    <SuperIntentInterpreter
      variant="compose"
      value={outcomeText}
      onChange={setOutcomeText}
      onSubmit={() => void interpretOutcome()}
      disabled={isWorking}
      isLoading={isInterpreting}
      error={error}
      showExamples={!plan}
    />
  );

  const describeCard = (
    <Collapsible open={describeOpen} onOpenChange={setDescribeOpen}>
      <Card className="border-0 shadow-none ring-0">
        <CardHeader className="pb-3 px-4 pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <ArtifactStateBadge artifactState={artifactState} composeMode={composeMode} />
            {(plan || interpretation) && (
              <div className="flex items-center gap-2">
                <Button actionTier="ghost" size="sm" className="h-8" onClick={handleReset}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  Start over
                </Button>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 gap-1">
                    {describeOpen ? (
                      <>
                        Collapse
                        <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
                      </>
                    ) : (
                      <>
                        Expand
                        <ChevronDown className="h-4 w-4 transition-transform" />
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
            )}
          </div>
          {!describeOpen && outcomeText && (
            <CardDescription className="line-clamp-2 mt-2">{outcomeText}</CardDescription>
          )}
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 px-4 pb-4">{describeEditor}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );

  const confirmPanel =
    phase === 'confirm' && interpretation ? (
      <IntentConfirmPanel
        interpretation={interpretation}
        composeMode={composeMode}
        isWorking={isWorking}
        onConfirmGuided={() => void confirmAndPlan()}
        onExpressProceed={() => void expressLaneProceed()}
        onReinterpret={() => void interpretOutcome()}
      />
    ) : null;

  const planCardInner = plan ? (
    <Card id="mission-plan-review" className="border-0 shadow-none ring-0 bg-card">
      <CardHeader className="pb-3 px-4 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Blocks className="h-5 w-5 ui-icon" />
            <CardTitle className="text-lg">
              {isMission ? PRODUCT_TERMS.mission : PRODUCT_TERMS.autoBlockFlow}
            </CardTitle>
          </div>
          <Badge variant="secondary">{confidencePct}% match confidence</Badge>
          {plan.plannerSource && (
            <Badge variant={plan.plannerSource === 'llm' ? 'default' : 'outline'}>
              {plan.plannerSource === 'llm' ? 'AI planner' : 'Rule planner'}
            </Badge>
          )}
          {plan.director?.pacing === 'journey_tube' && (
            <Badge variant="outline" className="border-primary/40">
              Journey Tube
            </Badge>
          )}
        </div>
        <CardDescription>
          {blockCount}{' '}
          {blockCount === 1 ? PRODUCT_TERMS.block.toLowerCase() : PRODUCT_TERMS.blocks.toLowerCase()} —{' '}
          {plan.interpretedGoal}
        </CardDescription>
        <Progress value={confidencePct} className="h-1.5 mt-2" />
        <div className="flex flex-wrap gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setShowOutcomeEditor((v) => !v)}
          >
            {showOutcomeEditor ? 'Hide outcome' : 'Edit outcome'}
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={handleReset}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Start over
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 px-4 pb-4">
        {showOutcomeEditor && (
          <div className="rounded-lg border border-dashed p-3">{describeEditor}</div>
        )}

        <PlanReadinessPanel plan={plan} onPlanUpdate={refreshPlanReadiness} />

        <MissionRiskReviewPanel plan={plan} />

        {showLinkedInPanel && (
          <LinkedInConnectorPanel onConnected={() => refreshPlanReadiness(plan)} />
        )}

        {showMcpPanel && (
          <McpConnectorPanel compact onConnected={() => refreshPlanReadiness(plan)} />
        )}

        {(plan.gmailInboxId || plan.useGmailInbound || plan.steps.some((s) => s.subtype === 'gmail_inbound')) && (
          <GmailConnectorPanel
            plan={plan}
            onInboxReady={(inboxId, postUrl) => {
              const next = attachWorkflowPreview({
                ...plan,
                gmailInboxId: inboxId,
                gmailPostUrl: postUrl,
                useGmailInbound: true,
              });
              refreshPlanReadiness(next);
            }}
          />
        )}

        {plan.resolvedConnectors && plan.resolvedConnectors.length > 0 && (
          <ConnectorLibraryPanel plan={plan} />
        )}

        <div className="space-y-3">
          {workflowPreview && workflowPreview.nodes.length > 0 && (
            <ComposePlanRowCard
              title="Flow map preview"
              description={`${workflowPreview.nodes.length} blocks wired — open the full map in Builder to edit`}
              icon={<Map />}
              action={
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-7 px-2 text-[11px] shrink-0"
                  onClick={openFlowMapInBuilder}
                >
                  Open Flow Map
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              }
              contentClassName="!mt-2"
            >
              <ComposeFlowMapPreview
                nodes={workflowPreview.nodes}
                edges={workflowPreview.edges}
              />
            </ComposePlanRowCard>
          )}

          {plan.steps.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-0.5">
                Planned execution order
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {plan.steps.map((step, i) => (
                  <ComposePlanRowCard
                    key={step.id}
                    title={`${i + 1}. ${step.label}`}
                    description={step.rationale}
                    icon={<Blocks className="h-4 w-4 ui-icon" />}
                    contentClassName="!mt-2"
                  >
                    <Badge variant="outline" className="text-[10px]">
                      {getBlockTypeLabel(step.type, step.subtype)}
                    </Badge>
                  </ComposePlanRowCard>
                ))}
              </div>
            </div>
          )}

          {plan.connectors.length > 0 && (
            <ComposePlanRowCard
              title="Connectors needed"
              description="Credentials and services this flow depends on"
              icon={<Plug />}
              contentClassName="!mt-2"
            >
              <div className="space-y-2">
                {plan.connectors.map((c) => (
                  <div key={c.id} className="rounded-md border border-border/50 bg-muted/20 p-2.5 text-sm">
                    <div className="flex flex-wrap items-center gap-2 mb-0.5">
                      <span className="font-medium">{c.name}</span>
                      <Badge
                        variant={c.status === 'needs_credentials' ? 'destructive' : 'secondary'}
                        className="text-[10px]"
                      >
                        {c.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-xs">{c.purpose}</p>
                  </div>
                ))}
              </div>
            </ComposePlanRowCard>
          )}

          {plan.apis.length > 0 && (
            <ComposePlanRowCard
              title="APIs identified"
              icon={<Globe />}
              contentClassName="!mt-2"
            >
              <ul className="space-y-1.5">
                {plan.apis.map((a) => (
                  <li key={a.id} className="text-sm rounded-md border border-border/50 bg-muted/20 p-2">
                    <span className="font-medium">{a.name}</span>
                    <span className="text-muted-foreground text-xs block">{a.purpose}</span>
                  </li>
                ))}
              </ul>
            </ComposePlanRowCard>
          )}

          {(plan.assumptions.length > 0 || plan.gaps.length > 0) && (
            <ComposePlanRowCard
              title="Assumptions & gaps"
              description="What the planner inferred or still needs"
              icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
              contentClassName="!mt-2"
            >
              <div className="grid sm:grid-cols-2 gap-2">
                {plan.assumptions.map((a) => (
                  <p key={a} className="text-xs text-muted-foreground flex gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-ab-green-sea" />
                    {a}
                  </p>
                ))}
                {plan.gaps.map((g) => (
                  <p key={g} className="text-xs text-amber-600 dark:text-amber-400 flex gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {g}
                  </p>
                ))}
              </div>
            </ComposePlanRowCard>
          )}
        </div>

        <Separator />

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => void runDryRunTest()}
            disabled={isDryRunning}
            className="w-full sm:w-auto"
          >
            <FlaskConical className="h-4 w-4 mr-2" />
            {isDryRunning ? 'Dry running…' : 'Dry run test'}
          </Button>
          <Button
            onClick={() => {
              approveAndOpenInBuilder();
              navigate(REMEDIATION.mission);
            }}
            disabled={!dryRunReport?.readyForReview}
            className="w-full sm:w-auto"
          >
            Approve & save {PRODUCT_TERMS.autoBlockFlow}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
        {!dryRunReport && (
          <p className="text-xs text-muted-foreground">
            Dry run first — then approve to crystallize your {PRODUCT_TERMS.autoBlockFlow} at rest.
          </p>
        )}
      </CardContent>
    </Card>
  ) : null;

  const dryRunCard = dryRunReport ? (
    <Card
      className={cn(
        'border-0 shadow-none ring-0',
        dryRunReport.passed ? 'bg-ab-green-mist/40 dark:bg-ab-green-forest/20' : 'bg-destructive/[0.03]'
      )}
    >
      <CardHeader className="pb-3 px-4 pt-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FlaskConical className="h-5 w-5" />
          <CardTitle className="text-lg">Dry run report</CardTitle>
          <Badge variant={dryRunReport.passed ? 'default' : 'destructive'}>
            {dryRunReport.passed ? 'Ready for review' : 'Issues found'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-4 pb-4">
        <ScrollArea className="h-[min(280px,45vh)] rounded-md border">
          <ul className="divide-y">
            {dryRunReport.stepResults
              .filter((s) => s.status !== 'warn' || s.message)
              .slice(0, 24)
              .map((step) => (
                <li key={step.nodeId} className="p-3 text-sm flex gap-3">
                  <StatusIcon status={step.status} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{step.label}</p>
                    {step.message && (
                      <p className="text-xs text-muted-foreground">{step.message}</p>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        </ScrollArea>
        {dryRunReport.readyForReview && (
          <Button
            className="w-full sm:w-auto"
            onClick={() => {
              approveAndOpenInBuilder();
              navigate(REMEDIATION.mission);
            }}
          >
            Approve & open Flow Map
            <Map className="h-4 w-4 ml-2" />
          </Button>
        )}
      </CardContent>
    </Card>
  ) : null;

  const workingCard = (
    <Card className="border-0 shadow-none ring-0 border-dashed bg-card">
      <CardContent className="py-10 flex flex-col items-center gap-3 text-muted-foreground">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-sm font-medium text-center">
          {phase === 'interpreting' && 'Interpreting your outcome…'}
          {phase === 'express_running' && 'Express lane — planning Mission…'}
          {(phase === 'director' || isDirectorWorking) && 'Director expanding your request…'}
          {phase === 'planning' && !isDirectorWorking && 'Planning your system…'}
          {isDryRunning && 'Simulating your Mission — dry run in progress…'}
        </p>
      </CardContent>
    </Card>
  );

  const flowReadyCard = (
    <Card className="border-0 shadow-none ring-0 bg-ab-green-mist/35 dark:bg-ab-green-forest/15">
      <CardContent className="py-6 px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-center sm:text-left">
          <p className="font-semibold flex items-center justify-center sm:justify-start gap-2">
            <CheckCircle2 className="h-5 w-5 text-ab-green-deep dark:text-ab-green-glow" />
            Your {PRODUCT_TERMS.autoBlockFlow} is ready
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            At rest — all steps known and repeatable. Open Flow Map in Builder to customize.
          </p>
        </div>
        <Button onClick={() => navigate(REMEDIATION.mission)}>
          Open Flow Map
          <Map className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  const showDirectorInNow =
    isDirectorWorking ||
    (isPlanning && phase === 'director') ||
    (Boolean(director ?? plan?.director) && timeline.current.id === 'review' && !dryRunReport);

  const renderNowContent = () => {
    if (timeline.isComplete) return flowReadyCard;
    if (autopilot && autopilotBusy) {
      return <AutopilotProgressCard phase={phase} isDryRunning={isDryRunning} />;
    }
    if (autopilot && !plan && !autopilotBusy) {
      return (
        <Card className="border-0 shadow-none ring-1 ring-destructive/20">
          <CardContent className="py-6 px-4 space-y-4">
            {error && (
              <ContextualError
                message={error}
                action={{ label: 'Retry autopilot', onClick: () => void runAutopilotPipeline() }}
                secondaryAction={{ label: 'Start over', onClick: handleReset }}
              />
            )}
            {describeEditor}
          </CardContent>
        </Card>
      );
    }
    if (finalPlanPanel) return finalPlanPanel;
    if (isDryRunning) return workingCard;
    if (timeline.current.id === 'approve' && dryRunReport) return dryRunCard;

    if (timeline.current.id === 'plan' && isWorking) {
      return (
        <>
          {showDirectorInNow && (
            <DirectorTrail
              director={director ?? plan?.director}
              brandedIdeas={plan?.brandedIdeas}
              isWorking={isDirectorWorking || (isPlanning && phase === 'director')}
            />
          )}
          {workingCard}
        </>
      );
    }

    if (plan && (timeline.current.id === 'review' || timeline.current.id === 'plan')) {
      return (
        <>
          {showDirectorInNow && (
            <DirectorTrail
              director={director ?? plan.director}
              brandedIdeas={plan.brandedIdeas}
              isWorking={false}
            />
          )}
          {planCardInner}
        </>
      );
    }

    if (phase === 'confirm' && interpretation) return confirmPanel;
    if (isWorking && !plan) {
      return (
        <>
          {showDirectorInNow && (
            <DirectorTrail
              director={director ?? plan?.director}
              brandedIdeas={plan?.brandedIdeas}
              isWorking={isDirectorWorking || (isPlanning && phase === 'director')}
            />
          )}
          {workingCard}
        </>
      );
    }
    return describeCard;
  };

  const nowLabel =
    isDryRunning
      ? 'Dry run test'
      : timeline.current.id === 'approve' && dryRunReport
        ? 'Review dry run & approve'
        : timeline.current.label;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-4xl mx-auto w-full">
      {!autopilot && (
        <ComposeStoryAnchor
          timeline={timeline}
          outcomeText={outcomeText}
          interpretedGoal={interpretation?.interpretedGoal ?? plan?.interpretedGoal}
          onJumpToNow={jumpToNow}
        />
      )}

      {!autopilot && <VaultUnlockInline compact />}

      <div className={cn('relative', !autopilot && 'compose-timeline-stack pl-0 sm:pl-2')}>
        {!autopilot && (
          <div
            className="compose-timeline-spine pointer-events-none absolute left-4 sm:left-5 top-0 bottom-8 w-px bg-gradient-to-b from-ab-green-sea/50 via-ab-green-teal/20 to-transparent hidden sm:block"
            aria-hidden
          />
        )}

        {autopilot ? (
          <motion.div
            key={autopilotBusy ? 'autopilot-busy' : 'autopilot-ready'}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="bg-card rounded-xl overflow-hidden min-h-[12rem]"
          >
            {renderNowContent()}
          </motion.div>
        ) : (
          <ComposeTimelineRail
            timeline={timeline}
            historySteps={isMission ? missionTrail : []}
            nowLabel={nowLabel}
            className="sm:pl-6"
          >
            <motion.div
              key={timeline.current.id + (isDryRunning ? '-running' : '') + (plan ? '-has-plan' : '')}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="bg-card rounded-xl overflow-hidden min-h-[12rem]"
            >
              {renderNowContent()}
            </motion.div>
          </ComposeTimelineRail>
        )}
      </div>
    </div>
  );
}
